/**
 * TransformBuilder.ts
 *
 * Builder for transform functionality of IX3 plugins.
 * Handles transform functions for triggers, actions, conditions, and optimization features.
 */

import type {PluginBaseInfo, ExtensionPoints, ExtractType} from './types';
import type {ExtensionKey} from '@packages/systems/ix3/schema';
import type {
  ActionTransformFunction,
  TriggerTransformFunction,
  ConditionTransformFunction,
  TargetTransformFunction,
  IX3TransformPlugin,
} from '@packages/systems/ix3/transforms';

/**
 * Builder for transformations of IX3 plugins.
 * Handles code generation, optimization, and transformation of interactions.
 *
 * @typeParam T - Extension points specification defining available extension types
 *
 * @example
 * // Provide only actions & triggers
 * const publish = new TransformBuilder<{
 *   actions: "class" | "style";
 *   triggers: "click" | "hover";
 * }>(pluginInfo);
 */
export class TransformBuilder<
  T extends Partial<ExtensionPoints> = Record<string, never>,
  TContext extends Record<string, unknown> = Record<string, unknown>,
> {
  private baseInfo: PluginBaseInfo;
  private triggerTransforms = new Map<
    ExtensionKey,
    TriggerTransformFunction<TContext>
  >();
  private targetTransforms = new Map<
    ExtensionKey,
    TargetTransformFunction<TContext>
  >();
  private conditionTransforms = new Map<
    ExtensionKey,
    ConditionTransformFunction<TContext>
  >();
  private actionTransforms = new Map<
    ExtensionKey,
    ActionTransformFunction<TContext>
  >();

  /**
   * Creates a new PublishBuilder
   * @param baseInfo - Shared plugin information
   */
  constructor(baseInfo: PluginBaseInfo) {
    this.baseInfo = baseInfo;
  }

  /**
   * Adds a context-aware transform for target resolvers with comprehensive logging
   * @param resolverType - The resolver type to transform
   * @param transform - Transform function that adapts based on context
   * @returns The builder instance for chaining
   */
  addTargetTransform<K extends ExtractType<T, 'targetResolvers'>>(
    resolverType: K,
    transform: TargetTransformFunction<TContext>
  ): this {
    this.targetTransforms.set(
      this.createExtensionKey(resolverType),
      function wrappedIx3TargetTransform(data, context, path) {
        return transform(data, context, path);
      }
    );
    return this;
  }

  /**
   * Adds a context-aware transform for triggers with comprehensive logging
   * @param triggerType - The trigger type to transform
   * @param transform - Transform function that adapts based on context
   * @returns The builder instance for chaining
   */
  addTriggerTransform<K extends ExtractType<T, 'triggers'>>(
    triggerType: K,
    transform: TriggerTransformFunction<TContext>
  ): this {
    this.triggerTransforms.set(
      this.createExtensionKey(triggerType),
      function wrappedIx3TriggerTransform(data, context, path) {
        return transform(data, context, path);
      }
    );
    return this;
  }

  /**
   * Adds a context-aware transform for conditions with comprehensive logging
   * @param conditionType - The condition type to transform
   * @param transform - Transform function that adapts based on context
   * @returns The builder instance for chaining
   */
  addConditionTransform<K extends ExtractType<T, 'conditions'>>(
    conditionType: K,
    transform: ConditionTransformFunction<TContext>
  ): this {
    this.conditionTransforms.set(
      this.createExtensionKey(conditionType),
      function wrappedIx3ConditionTransform(data, context, path) {
        return transform(data, context, path);
      }
    );
    return this;
  }

  /**
   * Adds a context-aware transform for actions with comprehensive logging
   * @param actionType - The action type to transform
   * @param transform - Transform function that adapts based on context
   * @returns The builder instance for chaining
   */
  addActionTransform<K extends ExtractType<T, 'actions'>>(
    actionType: K,
    transform: ActionTransformFunction<TContext>
  ): this {
    this.actionTransforms.set(
      this.createExtensionKey(actionType),
      function wrappedIx3ActionTransform(data, context, path) {
        return transform(data, context, path);
      }
    );
    return this;
  }

  /**
   * Creates an extension key for the transform
   * @param type - The transform type
   * @returns Extension key
   */
  private createExtensionKey<K>(type: K): ExtensionKey {
    return `${this.baseInfo.namespace}:${type}` as ExtensionKey;
  }

  /**
   * Creates the final TransformPlugin instance
   * @returns The configured publish plugin
   */
  buildTransform(): IX3TransformPlugin<TContext> {
    return {
      namespace: this.baseInfo.namespace,
      pluginId: this.baseInfo.pluginId,
      version: this.baseInfo.version,
      displayName: this.baseInfo.displayName,
      description: this.baseInfo.description,
      triggerTransforms: this.triggerTransforms,
      targetTransforms: this.targetTransforms,
      conditionTransforms: this.conditionTransforms,
      actionTransforms: this.actionTransforms,
    };
  }
}
